import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import T from "../styles/tokens";
import * as api from "../utils/api";
import { Modal } from "../components/UI";

// ─── Sign-in log → flat record (for CSV / Excel export) ──────────────────────
const SIGNIN_COLUMNS = [
  ["Timestamp",                 (it) => it.createdDateTime],
  ["User Display Name",         (it) => it.userDisplayName],
  ["User Principal Name",       (it) => it.userPrincipalName],
  ["User ID",                   (it) => it.userId],
  ["App Display Name",          (it) => it.appDisplayName],
  ["App ID",                    (it) => it.appId],
  ["IP Address",                (it) => it.ipAddress],
  ["Client App",                (it) => it.clientAppUsed],
  ["Is Interactive",            (it) => it.isInteractive],
  ["Correlation ID",            (it) => it.correlationId],
  ["Conditional Access Status", (it) => it.conditionalAccessStatus],
  ["Risk State",                (it) => it.riskState],
  ["Risk Level (Aggregated)",   (it) => it.riskLevelAggregated],
  ["Risk Level (During Sign-In)", (it) => it.riskLevelDuringSignIn],
  ["Risk Detail",               (it) => it.riskDetail],
  ["Risk Event Types",          (it) => (it.riskEventTypes || []).join("; ")],
  ["Resource Display Name",     (it) => it.resourceDisplayName],
  ["Resource ID",               (it) => it.resourceId],
  ["Status Code",               (it) => it.status?.errorCode],
  ["Status Failure Reason",     (it) => it.status?.failureReason],
  ["Status Additional Details", (it) => it.status?.additionalDetails],
  ["Device Display Name",       (it) => it.deviceDetail?.displayName],
  ["Device OS",                 (it) => it.deviceDetail?.operatingSystem],
  ["Device Browser",            (it) => it.deviceDetail?.browser],
  ["Device Compliant",          (it) => it.deviceDetail?.isCompliant],
  ["Device Managed",            (it) => it.deviceDetail?.isManaged],
  ["Trust Type",                (it) => it.deviceDetail?.trustType],
  ["City",                      (it) => it.location?.city],
  ["State",                     (it) => it.location?.state],
  ["Country",                   (it) => it.location?.countryOrRegion],
  ["Latitude",                  (it) => it.location?.geoCoordinates?.latitude],
  ["Longitude",                 (it) => it.location?.geoCoordinates?.longitude],
  ["Applied CA Policies",       (it) => (it.appliedConditionalAccessPolicies || []).map((p) => `${p.displayName} [${p.result}]`).join("; ")],
  ["Sign-In ID",                (it) => it.id],
];

function buildSignInRecords(items) {
  return items.map((it) => {
    const rec = {};
    for (const [header, accessor] of SIGNIN_COLUMNS) {
      const v = accessor(it);
      rec[header] = v == null ? "" : v;
    }
    return rec;
  });
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function exportSignInsCsv(items, filename) {
  const records = buildSignInRecords(items);
  const headers = SIGNIN_COLUMNS.map(([h]) => h);
  const escape = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v);
    if (s.includes(",") || s.includes("\"") || s.includes("\n") || s.includes("\r")) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  };
  const lines = [headers.join(",")].concat(
    records.map((r) => headers.map((h) => escape(r[h])).join(","))
  );
  // UTF-8 BOM so Excel detects encoding correctly when opening the .csv
  const blob = new Blob(["﻿" + lines.join("\r\n")], { type: "text/csv;charset=utf-8" });
  downloadBlob(blob, filename);
}

function exportSignInsXlsx(items, filename) {
  const records = buildSignInRecords(items);
  const headers = SIGNIN_COLUMNS.map(([h]) => h);
  const ws = XLSX.utils.json_to_sheet(records, { header: headers });
  // Auto-size columns (approximate — char width)
  ws["!cols"] = headers.map((h) => {
    const maxLen = Math.max(
      h.length,
      ...records.map((r) => String(r[h] ?? "").length)
    );
    return { wch: Math.min(60, Math.max(10, maxLen + 2)) };
  });
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Sign-In Logs");
  const out = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  downloadBlob(new Blob([out], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), filename);
}

// ─── Small inline UI ─────────────────────────────────────────────────────────
const STATUS_MAP = {
  healthy: { l: "Healthy", bg: T.greenBg, c: T.green },
  active: { l: "Active", bg: T.greenBg, c: T.green },
  warning: { l: "Warning", bg: T.amberBg, c: T.amber },
  critical: { l: "Critical", bg: T.redBg, c: T.red },
  failed: { l: "Failed", bg: T.redBg, c: T.red },
  success: { l: "Success", bg: T.greenBg, c: T.green },
  info: { l: "Info", bg: "#F0EAFF", c: T.purple },
};
const Badge = ({ status, label }) => {
  const s = STATUS_MAP[status] || STATUS_MAP.info;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px", borderRadius: 99, background: s.bg, color: s.c, fontSize: 10.5, fontWeight: 600, letterSpacing: "0.03em", fontFamily: T.font, whiteSpace: "nowrap" }}>
      <span style={{ width: 5, height: 5, borderRadius: "50%", background: s.c }} />
      {label || s.l}
    </span>
  );
};
const SectionCard = ({ title, badge, children, action }) => (
  <div style={{ background: T.white, borderRadius: T.radius, border: `1px solid ${T.gray200}`, overflow: "hidden" }}>
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 20px", borderBottom: `1px solid ${T.gray100}` }}>
      <h3 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: T.indigo, fontFamily: T.font, flex: 1 }}>{title}</h3>
      {badge}
      {action}
    </div>
    <div style={{ padding: "16px 20px" }}>{children}</div>
  </div>
);
const Tab = ({ active, label, onClick }) => (
  <button onClick={onClick} style={{
    padding: "8px 16px", borderRadius: 99, border: "none", cursor: "pointer",
    background: active ? T.purple : "transparent",
    color: active ? T.white : T.charcoal,
    fontSize: 12, fontWeight: 600, fontFamily: T.font, transition: "all 0.15s",
  }}>{label}</button>
);
const Btn = ({ children, onClick, variant = "primary", size = "md", disabled, type = "button" }) => {
  const bg = variant === "danger" ? T.red : variant === "secondary" ? T.white : variant === "ghost" ? "transparent" : T.purple;
  const fg = variant === "secondary" || variant === "ghost" ? T.purple : T.white;
  const border = variant === "secondary" ? `1px solid ${T.gray200}` : "none";
  const pad = size === "sm" ? "6px 12px" : "9px 18px";
  const fs = size === "sm" ? 11.5 : 13;
  return (
    <button type={type} onClick={onClick} disabled={disabled} style={{
      padding: pad, borderRadius: T.radiusSm, border, background: bg, color: fg,
      fontSize: fs, fontWeight: 600, fontFamily: T.font, cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.5 : 1, whiteSpace: "nowrap",
    }}>{children}</button>
  );
};
const Input = (props) => (
  <input {...props} style={{
    padding: "8px 12px", borderRadius: T.radiusSm, border: `1px solid ${T.gray200}`,
    fontSize: 13, fontFamily: T.font, color: T.charcoal, outline: "none", ...props.style,
  }} />
);
const Spinner = ({ size = 24 }) => (
  <div style={{ width: size, height: size, border: `2px solid ${T.gray200}`, borderTopColor: T.purple, borderRadius: "50%", animation: "pfSpin 0.8s linear infinite" }} />
);
const EmptyHint = ({ note }) => (
  <div style={{ fontSize: 13, color: T.gray400, fontFamily: T.font, fontStyle: "italic", padding: "24px 0", textAlign: "center" }}>{note}</div>
);
const ErrorBanner = ({ error, hint }) => (
  <div style={{ background: T.redBg, color: T.red, padding: "10px 14px", borderRadius: T.radiusSm, fontSize: 12.5, fontFamily: T.font, marginBottom: 12 }}>
    <strong>{error}</strong>
    {hint && <div style={{ marginTop: 4, fontSize: 11.5, color: T.charcoal }}>{hint}</div>}
  </div>
);

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
};
const SKU_NAMES = {
  ENTERPRISEPACK: "M365 E3",
  ENTERPRISEPREMIUM: "M365 E5",
  SPB: "M365 Business Premium",
  O365_BUSINESS_PREMIUM: "M365 Business Standard",
  O365_BUSINESS_ESSENTIALS: "M365 Business Basic",
  EXCHANGESTANDARD: "Exchange Online P1",
  EXCHANGEENTERPRISE: "Exchange Online P2",
  EMS: "EMS E3",
  EMSPREMIUM: "EMS E5",
  AAD_PREMIUM: "Entra ID P1",
  AAD_PREMIUM_P2: "Entra ID P2",
  DEFENDER_ENDPOINT_P1: "Defender for Endpoint P1",
  DEFENDER_ENDPOINT_P2: "Defender for Endpoint P2",
};
const skuLabel = (sku) => SKU_NAMES[sku] || sku;

// ═══════════════════════════════════════════════════════════════════════════
//  License Management
// ═══════════════════════════════════════════════════════════════════════════
function LicensesTab({ clientId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await api.getClientLicenses(clientId);
      setData(d);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const toggleLicense = async (sku) => {
    if (!selectedUser) return;
    const hasIt = selectedUser.licenses.some((l) => l.skuId === sku.skuId);
    setBusy(true);
    setError(null);
    try {
      await api.assignClientLicense(clientId, {
        userId: selectedUser.id,
        addSkuIds: hasIt ? [] : [sku.skuId],
        removeSkuIds: hasIt ? [sku.skuId] : [],
      });
      await load();
      const refreshed = data?.users.find((u) => u.id === selectedUser.id);
      if (refreshed) setSelectedUser(refreshed);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (selectedUser && data) {
      const refreshed = data.users.find((u) => u.id === selectedUser.id);
      if (refreshed) setSelectedUser(refreshed);
    }
  }, [data]);

  if (loading) return <div style={{ display: "flex", justifyContent: "center", padding: 40 }}><Spinner /></div>;
  if (error) return <ErrorBanner error={error} />;
  if (!data) return null;

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <SectionCard title="Subscribed SKUs" badge={<span style={{ fontSize: 11, color: T.gray400 }}>{data.skus.length} SKUs</span>}>
        <div className="pf-table-wrap">
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, fontFamily: T.font }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${T.gray200}` }}>
              {["SKU", "Purchased", "Assigned", "Unassigned"].map((h) => (
                <th key={h} style={{ textAlign: "left", padding: "8px 10px", fontSize: 10.5, fontWeight: 600, color: T.gray400, textTransform: "uppercase", letterSpacing: "0.08em" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.skus.map((s) => (
              <tr key={s.skuId} style={{ borderBottom: `1px solid ${T.gray100}` }}>
                <td style={{ padding: "10px", fontWeight: 600, color: T.indigo }}>{skuLabel(s.skuPartNumber)}</td>
                <td style={{ padding: "10px", color: T.charcoal }}>{s.purchased}</td>
                <td style={{ padding: "10px", color: T.green }}>{s.assigned}</td>
                <td style={{ padding: "10px", color: s.unassigned > 0 ? T.amber : T.charcoal, fontWeight: s.unassigned > 0 ? 600 : 400 }}>{s.unassigned}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </SectionCard>

      <div className="pf-stack-mobile" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <SectionCard title="Users" badge={<span style={{ fontSize: 11, color: T.gray400 }}>{data.users.length} users</span>}>
          <div style={{ maxHeight: 480, overflowY: "auto" }}>
            {data.users.map((u) => (
              <button key={u.id} onClick={() => setSelectedUser(u)} style={{
                width: "100%", textAlign: "left", padding: "10px 12px", border: "none",
                background: selectedUser?.id === u.id ? "#F8F5FF" : "transparent",
                borderBottom: `1px solid ${T.gray100}`, cursor: "pointer", fontFamily: T.font,
              }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: T.indigo }}>{u.displayName || u.upn}</div>
                <div style={{ fontSize: 11, color: T.gray400 }}>{u.upn} · {u.licenses.length} license{u.licenses.length === 1 ? "" : "s"}</div>
              </button>
            ))}
          </div>
        </SectionCard>

        <SectionCard title={selectedUser ? selectedUser.displayName || selectedUser.upn : "Select a user"} badge={selectedUser && <Badge status={selectedUser.accountEnabled === false ? "warning" : "active"} label={selectedUser.accountEnabled === false ? "Disabled" : "Enabled"} />}>
          {!selectedUser ? (
            <EmptyHint note="Click a user on the left to view and manage their licenses." />
          ) : (
            <>
              <div style={{ fontSize: 11.5, color: T.gray400, fontFamily: T.font, marginBottom: 12 }}>{selectedUser.upn}</div>
              {busy && <div style={{ fontSize: 11.5, color: T.purple, marginBottom: 8 }}>Updating…</div>}
              <div style={{ display: "grid", gap: 6 }}>
                {data.skus.map((s) => {
                  const has = selectedUser.licenses.some((l) => l.skuId === s.skuId);
                  const noStock = !has && s.unassigned === 0;
                  return (
                    <div key={s.skuId} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", border: `1px solid ${has ? T.purple : T.gray200}`, borderRadius: T.radiusSm, background: has ? "#F8F5FF" : T.white }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 12.5, fontWeight: 600, color: T.indigo, fontFamily: T.font }}>{skuLabel(s.skuPartNumber)}</div>
                        <div style={{ fontSize: 10.5, color: T.gray400 }}>{s.unassigned} of {s.purchased} available</div>
                      </div>
                      <Btn size="sm" variant={has ? "danger" : "primary"} onClick={() => toggleLicense(s)} disabled={busy || (!has && noStock)}>
                        {has ? "Remove" : noStock ? "No stock" : "Assign"}
                      </Btn>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </SectionCard>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  Role Management
// ═══════════════════════════════════════════════════════════════════════════
function RolesTab({ clientId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [addPickerOpen, setAddPickerOpen] = useState(false);
  const [roleSearch, setRoleSearch] = useState("");
  const [userLookup, setUserLookup] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await api.getClientRoles(clientId);
      setData(d);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const filteredUsers = useMemo(() => {
    if (!data) return [];
    if (!search) return data.users;
    const s = search.toLowerCase();
    return data.users.filter((u) => (u.displayName || "").toLowerCase().includes(s) || (u.upn || "").toLowerCase().includes(s));
  }, [data, search]);

  const filteredRoles = useMemo(() => {
    if (!data) return [];
    const taken = new Set((selectedUser?.roles || []).map((r) => r.roleTemplateId));
    return data.availableRoles
      .filter((r) => !taken.has(r.id))
      .filter((r) => !roleSearch || (r.displayName || "").toLowerCase().includes(roleSearch.toLowerCase()));
  }, [data, selectedUser, roleSearch]);

  useEffect(() => {
    if (selectedUser && data) {
      const refreshed = data.users.find((u) => u.userId === selectedUser.userId);
      if (refreshed) setSelectedUser(refreshed);
    }
  }, [data]);

  const addRole = async (role) => {
    setBusy(true);
    setError(null);
    try {
      await api.assignClientRole(clientId, { userId: selectedUser.userId, roleTemplateId: role.id });
      await load();
      setAddPickerOpen(false);
      setRoleSearch("");
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const removeRole = async (assignmentId) => {
    if (!window.confirm("Remove this role assignment?")) return;
    setBusy(true);
    setError(null);
    try {
      await api.removeClientRole(clientId, assignmentId);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div style={{ display: "flex", justifyContent: "center", padding: 40 }}><Spinner /></div>;
  if (error && !data) return <ErrorBanner error={error} />;
  if (!data) return null;

  return (
    <div style={{ display: "grid", gap: 16 }}>
      {error && <ErrorBanner error={error} />}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <SectionCard title="Users with Role Assignments" badge={<span style={{ fontSize: 11, color: T.gray400 }}>{data.users.length}</span>}>
          <Input placeholder="Search by name or UPN…" value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: "100%", marginBottom: 10 }} />
          <div style={{ maxHeight: 480, overflowY: "auto" }}>
            {filteredUsers.length === 0 ? (
              <EmptyHint note="No matching users." />
            ) : filteredUsers.map((u) => (
              <button key={u.userId} onClick={() => setSelectedUser(u)} style={{
                width: "100%", textAlign: "left", padding: "10px 12px", border: "none",
                background: selectedUser?.userId === u.userId ? "#F8F5FF" : "transparent",
                borderBottom: `1px solid ${T.gray100}`, cursor: "pointer", fontFamily: T.font,
              }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: T.indigo }}>{u.displayName || u.upn || u.userId}</div>
                <div style={{ fontSize: 11, color: T.gray400 }}>{u.upn || u.mail || "—"} · {u.roles.length} role{u.roles.length === 1 ? "" : "s"}</div>
              </button>
            ))}
          </div>
        </SectionCard>

        <SectionCard title={selectedUser ? selectedUser.displayName || selectedUser.upn : "Select a user"} action={selectedUser && <Btn size="sm" variant="secondary" onClick={() => setAddPickerOpen(true)}>+ Add Role</Btn>}>
          {!selectedUser ? (
            <EmptyHint note="Click a user on the left to view their directory role assignments." />
          ) : (
            <>
              <div style={{ fontSize: 11.5, color: T.gray400, fontFamily: T.font, marginBottom: 12 }}>{selectedUser.upn}</div>
              {busy && <div style={{ fontSize: 11.5, color: T.purple, marginBottom: 8 }}>Updating…</div>}
              {selectedUser.roles.length === 0 ? (
                <EmptyHint note="No role assignments." />
              ) : (
                <div style={{ display: "grid", gap: 6 }}>
                  {selectedUser.roles.map((r) => (
                    <div key={r.assignmentId} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", border: `1px solid ${T.gray200}`, borderRadius: T.radiusSm }}>
                      <div style={{ flex: 1, fontSize: 12.5, fontWeight: 600, color: T.indigo, fontFamily: T.font }}>{r.roleName || r.roleDefinitionId}</div>
                      <Btn size="sm" variant="danger" onClick={() => removeRole(r.assignmentId)} disabled={busy}>Remove</Btn>
                    </div>
                  ))}
                </div>
              )}
              {addPickerOpen && (
                <div style={{ marginTop: 16, padding: 14, background: T.gray100, borderRadius: T.radiusSm }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10 }}>
                    <Input placeholder="Search roles…" value={roleSearch} onChange={(e) => setRoleSearch(e.target.value)} style={{ flex: 1 }} autoFocus />
                    <Btn size="sm" variant="ghost" onClick={() => { setAddPickerOpen(false); setRoleSearch(""); }}>Cancel</Btn>
                  </div>
                  <div style={{ maxHeight: 280, overflowY: "auto" }}>
                    {filteredRoles.length === 0 ? (
                      <EmptyHint note="No matching roles available." />
                    ) : filteredRoles.map((r) => (
                      <button key={r.id} onClick={() => addRole(r)} disabled={busy} style={{
                        display: "block", width: "100%", textAlign: "left", padding: "8px 12px",
                        background: T.white, border: `1px solid ${T.gray200}`, borderRadius: T.radiusSm,
                        marginBottom: 4, cursor: "pointer", fontFamily: T.font,
                      }}>
                        <div style={{ fontSize: 12.5, fontWeight: 600, color: T.indigo }}>{r.displayName}</div>
                        {r.description && <div style={{ fontSize: 10.5, color: T.gray400, marginTop: 2 }}>{r.description}</div>}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </SectionCard>
      </div>

      <SectionCard title="Assign role to user not in the list">
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <Input placeholder="Paste user object ID (oid)…" value={userLookup} onChange={(e) => setUserLookup(e.target.value)} style={{ flex: 1 }} />
          <Btn size="sm" onClick={() => { setSelectedUser({ userId: userLookup, displayName: userLookup, roles: [] }); setAddPickerOpen(true); setUserLookup(""); }} disabled={!userLookup}>Open Role Picker</Btn>
        </div>
        <div style={{ fontSize: 11, color: T.gray400, marginTop: 6 }}>The list above only includes users who already have a role. Use this to add a first role to a user.</div>
      </SectionCard>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  Logs (Sign-in + Audit) — shared
// ═══════════════════════════════════════════════════════════════════════════
function LogsTab({ clientId, kind }) {
  // kind: "signin" | "audit"
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hint, setHint] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [filters, setFilters] = useState({
    from: new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10),
    to: "",
    user: "",
    status: "",
  });

  const run = useCallback(async () => {
    setLoading(true);
    setError(null);
    setHint(null);
    try {
      const params = {
        from: filters.from ? `${filters.from}T00:00:00Z` : undefined,
        to: filters.to ? `${filters.to}T23:59:59Z` : undefined,
        user: filters.user || undefined,
        status: kind === "signin" ? filters.status || undefined : undefined,
        limit: 200,
      };
      const d = kind === "signin" ? await api.getClientSignInLogs(clientId, params) : await api.getClientAuditLogs(clientId, params);
      setItems(kind === "signin" ? d.signIns || [] : d.events || []);
    } catch (err) {
      setError(err.message);
      setHint(err.hint || null);
    } finally {
      setLoading(false);
    }
  }, [clientId, kind, filters]);

  useEffect(() => { run(); /* run on mount only */ }, []);

  if (error) {
    return (
      <div style={{ display: "grid", gap: 12 }}>
        <ErrorBanner error={error} hint={hint || "Requires AuditLog.Read.All (Application) consented in the client tenant. Add the permission in your app registration and grant admin consent in the client's Entra portal."} />
        <Btn variant="secondary" size="sm" onClick={run}>Retry</Btn>
      </div>
    );
  }

  return (
    <div style={{ display: "grid", gap: 12 }}>
      <SectionCard title={kind === "signin" ? "Filter Sign-ins" : "Filter Audit Events"} action={<Btn size="sm" onClick={run} disabled={loading}>{loading ? "Loading…" : "Run"}</Btn>}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8 }}>
          <div>
            <label style={{ fontSize: 10.5, fontWeight: 600, color: T.gray400, textTransform: "uppercase", letterSpacing: "0.08em", fontFamily: T.font, display: "block", marginBottom: 4 }}>From</label>
            <Input type="date" value={filters.from} onChange={(e) => setFilters({ ...filters, from: e.target.value })} style={{ width: "100%" }} />
          </div>
          <div>
            <label style={{ fontSize: 10.5, fontWeight: 600, color: T.gray400, textTransform: "uppercase", letterSpacing: "0.08em", fontFamily: T.font, display: "block", marginBottom: 4 }}>To</label>
            <Input type="date" value={filters.to} onChange={(e) => setFilters({ ...filters, to: e.target.value })} style={{ width: "100%" }} />
          </div>
          <div>
            <label style={{ fontSize: 10.5, fontWeight: 600, color: T.gray400, textTransform: "uppercase", letterSpacing: "0.08em", fontFamily: T.font, display: "block", marginBottom: 4 }}>User (UPN prefix)</label>
            <Input placeholder="john@..." value={filters.user} onChange={(e) => setFilters({ ...filters, user: e.target.value })} style={{ width: "100%" }} />
          </div>
          {kind === "signin" && (
            <div>
              <label style={{ fontSize: 10.5, fontWeight: 600, color: T.gray400, textTransform: "uppercase", letterSpacing: "0.08em", fontFamily: T.font, display: "block", marginBottom: 4 }}>Status</label>
              <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} style={{ width: "100%", padding: "8px 12px", borderRadius: T.radiusSm, border: `1px solid ${T.gray200}`, fontSize: 13, fontFamily: T.font, color: T.charcoal }}>
                <option value="">All</option>
                <option value="success">Success</option>
                <option value="failed">Failed</option>
              </select>
            </div>
          )}
        </div>
      </SectionCard>

      <SectionCard
        title={kind === "signin" ? "Sign-in Events" : "Directory Audit Events"}
        badge={<span style={{ fontSize: 11, color: T.gray400 }}>{items.length} results</span>}
        action={kind === "signin" && items.length > 0 ? (
          <span style={{ display: "inline-flex", gap: 6 }}>
            <Btn size="sm" variant="secondary" onClick={() => exportSignInsCsv(items, `signin-logs-${new Date().toISOString().slice(0, 10)}.csv`)}>Export CSV</Btn>
            <Btn size="sm" variant="secondary" onClick={() => exportSignInsXlsx(items, `signin-logs-${new Date().toISOString().slice(0, 10)}.xlsx`)}>Export Excel</Btn>
          </span>
        ) : null}
      >
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 24 }}><Spinner /></div>
        ) : items.length === 0 ? (
          <EmptyHint note="No events match the current filters." />
        ) : (
          <div className="pf-table-wrap" style={{ maxHeight: 600, overflowY: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, fontFamily: T.font }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${T.gray200}` }}>
                  {kind === "signin"
                    ? ["Time", "User", "App", "Status", "IPv4 Address", ""].map((h) => <th key={h} style={{ textAlign: "left", padding: "8px 10px", fontSize: 10, fontWeight: 600, color: T.gray400, textTransform: "uppercase" }}>{h}</th>)
                    : ["Time", "Activity", "Initiator", "Result", ""].map((h) => <th key={h} style={{ textAlign: "left", padding: "8px 10px", fontSize: 10, fontWeight: 600, color: T.gray400, textTransform: "uppercase" }}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {items.map((it, i) => {
                  const open = !!expanded[i];
                  return (
                    <React.Fragment key={i}>
                      <tr onClick={() => setExpanded({ ...expanded, [i]: !open })} style={{ borderBottom: `1px solid ${T.gray100}`, cursor: "pointer" }}>
                        {kind === "signin" ? (
                          <>
                            <td style={{ padding: "8px 10px", color: T.charcoal }}>{fmtDateTime(it.createdDateTime)}</td>
                            <td style={{ padding: "8px 10px", color: T.indigo, fontWeight: 500 }}>{it.userPrincipalName}</td>
                            <td style={{ padding: "8px 10px", color: T.charcoal }}>{it.appDisplayName}</td>
                            <td style={{ padding: "8px 10px" }}><Badge status={it.status?.errorCode === 0 ? "success" : "failed"} /></td>
                            <td style={{ padding: "8px 10px", color: it.ipAddress ? T.charcoal : T.gray400, fontFamily: "monospace", fontSize: 11 }}>{it.ipAddress || "—"}</td>
                            <td style={{ padding: "8px 10px", color: T.gray400 }}>{open ? "▾" : "▸"}</td>
                          </>
                        ) : (
                          <>
                            <td style={{ padding: "8px 10px", color: T.charcoal }}>{fmtDateTime(it.activityDateTime)}</td>
                            <td style={{ padding: "8px 10px", color: T.indigo, fontWeight: 500 }}>{it.activityDisplayName}</td>
                            <td style={{ padding: "8px 10px", color: T.charcoal }}>{it.initiatedBy?.user?.userPrincipalName || it.initiatedBy?.app?.displayName || "—"}</td>
                            <td style={{ padding: "8px 10px" }}><Badge status={it.result === "success" ? "success" : it.result === "failure" ? "failed" : "info"} /></td>
                            <td style={{ padding: "8px 10px", color: T.gray400 }}>{open ? "▾" : "▸"}</td>
                          </>
                        )}
                      </tr>
                      {open && (
                        <tr>
                          <td colSpan={kind === "signin" ? 6 : 5} style={{ padding: "12px 14px", background: T.gray100 }}>
                            <pre style={{ margin: 0, fontSize: 11, fontFamily: "monospace", color: T.charcoal, whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: 300, overflowY: "auto" }}>{JSON.stringify(it, null, 2)}</pre>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  Message Trace
// ═══════════════════════════════════════════════════════════════════════════
function MailTraceTab({ clientId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [note, setNote] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [filters, setFilters] = useState({
    from: new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10),
    to: "",
  });

  const [errorHint, setErrorHint] = useState(null);
  const run = useCallback(async () => {
    setLoading(true);
    setError(null);
    setErrorHint(null);
    try {
      const d = await api.getClientMailTrace(clientId, {
        from: filters.from ? `${filters.from}T00:00:00Z` : undefined,
        to: filters.to ? `${filters.to}T23:59:59Z` : undefined,
        limit: 100,
      });
      setItems(d.alerts || []);
      setNote(d._note || null);
    } catch (err) {
      setError(err.message);
      setErrorHint(err.hint || null);
    } finally {
      setLoading(false);
    }
  }, [clientId, filters]);
  useEffect(() => { run(); /* run on mount only */ }, []);

  return (
    <div style={{ display: "grid", gap: 12 }}>
      <SectionCard title="Microsoft Graph limitation">
        <div style={{ fontSize: 12.5, color: T.charcoal, lineHeight: 1.6, fontFamily: T.font }}>
          Full Exchange Online message trace (sender/recipient/subject/delivery status across all mail flow) is not exposed through Microsoft Graph app-only auth. This view shows <strong>email-related security alerts</strong> from Microsoft Defender for Office 365 instead. For full message trace, use Exchange Online PowerShell's <code style={{ background: T.gray100, padding: "1px 6px", borderRadius: 3 }}>Get-MessageTrace</code>.
        </div>
      </SectionCard>

      {error && <ErrorBanner error={error} hint={errorHint || "Requires SecurityEvents.Read.All in the client tenant. For most Defender for O365 alerts, also requires SecurityAlert.Read.All."} />}

      <SectionCard title="Filters" action={<Btn size="sm" onClick={run} disabled={loading}>{loading ? "Loading…" : "Run"}</Btn>}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <div>
            <label style={{ fontSize: 10.5, fontWeight: 600, color: T.gray400, textTransform: "uppercase", letterSpacing: "0.08em", fontFamily: T.font, display: "block", marginBottom: 4 }}>From</label>
            <Input type="date" value={filters.from} onChange={(e) => setFilters({ ...filters, from: e.target.value })} style={{ width: "100%" }} />
          </div>
          <div>
            <label style={{ fontSize: 10.5, fontWeight: 600, color: T.gray400, textTransform: "uppercase", letterSpacing: "0.08em", fontFamily: T.font, display: "block", marginBottom: 4 }}>To</label>
            <Input type="date" value={filters.to} onChange={(e) => setFilters({ ...filters, to: e.target.value })} style={{ width: "100%" }} />
          </div>
        </div>
      </SectionCard>

      <SectionCard title="Email Alerts" badge={<span style={{ fontSize: 11, color: T.gray400 }}>{items.length} results</span>}>
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 24 }}><Spinner /></div>
        ) : items.length === 0 ? (
          <EmptyHint note={note ? "No alerts in range." : "No email alerts found."} />
        ) : (
          <div style={{ maxHeight: 600, overflowY: "auto" }}>
            {items.map((a, i) => {
              const open = !!expanded[i];
              return (
                <div key={a.id || i} style={{ borderBottom: `1px solid ${T.gray100}`, padding: "10px 0" }}>
                  <div onClick={() => setExpanded({ ...expanded, [i]: !open })} style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 12 }}>
                    <Badge status={a.severity === "high" ? "critical" : a.severity === "medium" ? "warning" : "info"} label={a.severity || "—"} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: T.indigo, fontFamily: T.font }}>{a.title || a.displayName || "(no title)"}</div>
                      <div style={{ fontSize: 11, color: T.gray400, marginTop: 2 }}>{fmtDateTime(a.createdDateTime)} · {a.status || "—"}</div>
                    </div>
                    <span style={{ color: T.gray400 }}>{open ? "▾" : "▸"}</span>
                  </div>
                  {open && (
                    <pre style={{ marginTop: 10, fontSize: 11, fontFamily: "monospace", color: T.charcoal, whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: 300, overflowY: "auto", background: T.gray100, padding: 12, borderRadius: T.radiusSm }}>{JSON.stringify(a, null, 2)}</pre>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </SectionCard>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  Users
// ═══════════════════════════════════════════════════════════════════════════
function UsersTab({ clientId }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorHint, setErrorHint] = useState(null);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState(null); // staged edit fields

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setErrorHint(null);
    try {
      const d = await api.listClientTenantUsers(clientId);
      setUsers(d.users || []);
    } catch (err) {
      setError(err.message);
      setErrorHint(err.hint || null);
    } finally {
      setLoading(false);
    }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const selected = useMemo(() => users.find((u) => u.id === selectedId) || null, [users, selectedId]);
  const filtered = useMemo(() => {
    if (!search) return users;
    const s = search.toLowerCase();
    return users.filter((u) =>
      (u.displayName || "").toLowerCase().includes(s) ||
      (u.userPrincipalName || "").toLowerCase().includes(s) ||
      (u.mail || "").toLowerCase().includes(s)
    );
  }, [users, search]);

  useEffect(() => {
    if (selected) setEdit({
      displayName: selected.displayName || "",
      givenName: selected.givenName || "",
      surname: selected.surname || "",
      jobTitle: selected.jobTitle || "",
      department: selected.department || "",
      accountEnabled: selected.accountEnabled !== false,
    });
    else setEdit(null);
  }, [selectedId]); // eslint-disable-line

  const saveEdit = async () => {
    setBusy(true);
    try {
      await api.updateClientUser(clientId, selectedId, edit);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div style={{ display: "flex", justifyContent: "center", padding: 40 }}><Spinner /></div>;
  if (error && users.length === 0) return <ErrorBanner error={error} hint={errorHint} />;

  return (
    <div style={{ display: "grid", gap: 16 }}>
      {error && <ErrorBanner error={error} hint={errorHint} />}
      <div className="pf-stack-mobile" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <SectionCard
          title="Users"
          badge={<span style={{ fontSize: 11, color: T.gray400 }}>{filtered.length} / {users.length}</span>}
          action={<Btn size="sm" onClick={() => setCreateOpen(true)}>+ New User</Btn>}
        >
          <Input placeholder="Search by name, UPN, or mail…" value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: "100%", marginBottom: 10 }} />
          <div style={{ maxHeight: 480, overflowY: "auto" }}>
            {filtered.length === 0 ? <EmptyHint note="No users match the filter." /> : filtered.map((u) => (
              <button key={u.id} onClick={() => setSelectedId(u.id)} style={{
                width: "100%", textAlign: "left", padding: "10px 12px", border: "none",
                background: selectedId === u.id ? "#F8F5FF" : "transparent",
                borderBottom: `1px solid ${T.gray100}`, cursor: "pointer", fontFamily: T.font,
              }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: T.indigo }}>{u.displayName || u.userPrincipalName}</div>
                  {u.accountEnabled === false && <Badge status="warning" label="Disabled" />}
                  {u.userType === "Guest" && <Badge status="info" label="Guest" />}
                </div>
                <div style={{ fontSize: 11, color: T.gray400 }}>{u.userPrincipalName}</div>
              </button>
            ))}
          </div>
        </SectionCard>

        <SectionCard
          title={selected ? selected.displayName || selected.userPrincipalName : "Select a user"}
          badge={selected && (selected.accountEnabled === false ? <Badge status="warning" label="Disabled" /> : <Badge status="active" />)}
        >
          {!selected || !edit ? (
            <EmptyHint note="Click a user to view and edit." />
          ) : (
            <>
              <div style={{ fontSize: 11.5, color: T.gray400, fontFamily: T.font, marginBottom: 14 }}>{selected.userPrincipalName}</div>
              {busy && <div style={{ fontSize: 11.5, color: T.purple, marginBottom: 8 }}>Saving…</div>}
              <div style={{ display: "grid", gap: 10 }}>
                {[
                  ["Display name", "displayName"],
                  ["Given name", "givenName"],
                  ["Surname", "surname"],
                  ["Job title", "jobTitle"],
                  ["Department", "department"],
                ].map(([label, key]) => (
                  <div key={key}>
                    <label style={{ fontSize: 10.5, fontWeight: 600, color: T.gray400, textTransform: "uppercase", letterSpacing: "0.08em", fontFamily: T.font, display: "block", marginBottom: 4 }}>{label}</label>
                    <Input value={edit[key] || ""} onChange={(e) => setEdit({ ...edit, [key]: e.target.value })} style={{ width: "100%" }} />
                  </div>
                ))}
                <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: T.charcoal, fontFamily: T.font, cursor: "pointer" }}>
                  <input type="checkbox" checked={!!edit.accountEnabled} onChange={(e) => setEdit({ ...edit, accountEnabled: e.target.checked })} style={{ accentColor: T.purple }} />
                  Account enabled
                </label>
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 18, flexWrap: "wrap" }}>
                <Btn size="sm" onClick={saveEdit} disabled={busy}>Save Changes</Btn>
                <Btn size="sm" variant="secondary" onClick={() => setResetOpen(true)}>Reset Password</Btn>
                <Btn size="sm" variant="danger" onClick={() => setDeleteOpen(true)}>Delete User</Btn>
              </div>
            </>
          )}
        </SectionCard>
      </div>

      <CreateUserModal open={createOpen} clientId={clientId} onClose={() => setCreateOpen(false)} onCreated={(u) => { setCreateOpen(false); load(); setSelectedId(u.id); }} />
      <ResetPasswordModal open={resetOpen} clientId={clientId} userId={selectedId} userLabel={selected?.userPrincipalName} onClose={() => setResetOpen(false)} />
      <DeleteUserModal open={deleteOpen} clientId={clientId} user={selected} onClose={() => setDeleteOpen(false)} onDeleted={() => { setDeleteOpen(false); setSelectedId(null); load(); }} />
    </div>
  );
}

function CreateUserModal({ open, clientId, onClose, onCreated }) {
  const [form, setForm] = useState({ displayName: "", mailNickname: "", userPrincipalName: "", password: "", jobTitle: "", department: "", forceChangePasswordNextSignIn: true });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => { if (open) { setForm({ displayName: "", mailNickname: "", userPrincipalName: "", password: "", jobTitle: "", department: "", forceChangePasswordNextSignIn: true }); setError(null); } }, [open]);
  const submit = async () => {
    setBusy(true); setError(null);
    try {
      const { user } = await api.createClientUser(clientId, form);
      onCreated(user);
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };
  return (
    <Modal open={open} onClose={onClose} title="Create User" width={520}>
      <div style={{ fontFamily: T.font, display: "grid", gap: 10 }}>
        {error && <div style={{ background: T.redBg, color: T.red, padding: 10, borderRadius: T.radiusSm, fontSize: 12 }}>{error}</div>}
        {[
          ["Display Name *", "displayName", "Jane Doe"],
          ["Mail Nickname * (alias)", "mailNickname", "jdoe"],
          ["User Principal Name *", "userPrincipalName", "jdoe@example.com"],
          ["Initial Password *", "password", "P4ssword!"],
          ["Job Title", "jobTitle", "Project Manager"],
          ["Department", "department", "Engineering"],
        ].map(([label, key, placeholder]) => (
          <div key={key}>
            <label style={{ fontSize: 10.5, fontWeight: 600, color: T.gray400, textTransform: "uppercase", letterSpacing: "0.08em", display: "block", marginBottom: 4 }}>{label}</label>
            <Input value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} placeholder={placeholder} type={key === "password" ? "text" : "text"} style={{ width: "100%" }} />
          </div>
        ))}
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: T.charcoal, cursor: "pointer" }}>
          <input type="checkbox" checked={form.forceChangePasswordNextSignIn} onChange={(e) => setForm({ ...form, forceChangePasswordNextSignIn: e.target.checked })} style={{ accentColor: T.purple }} />
          Force password change at next sign-in
        </label>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 10 }}>
          <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
          <Btn onClick={submit} disabled={busy}>{busy ? "Creating…" : "Create User"}</Btn>
        </div>
      </div>
    </Modal>
  );
}

function ResetPasswordModal({ open, clientId, userId, userLabel, onClose }) {
  const [newPassword, setNewPassword] = useState("");
  const [forceChange, setForceChange] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);
  useEffect(() => { if (open) { setNewPassword(""); setForceChange(true); setError(null); setDone(false); } }, [open]);
  const submit = async () => {
    setBusy(true); setError(null);
    try {
      await api.resetClientUserPassword(clientId, userId, { newPassword, forceChangePasswordNextSignIn: forceChange });
      setDone(true);
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };
  return (
    <Modal open={open} onClose={onClose} title="Reset Password" width={460}>
      <div style={{ fontFamily: T.font }}>
        {done ? (
          <>
            <div style={{ background: "#E8F8F0", color: T.green, padding: 12, borderRadius: T.radiusSm, fontSize: 13, marginBottom: 12 }}>Password reset. Share the new password securely with the user.</div>
            <div style={{ display: "flex", justifyContent: "flex-end" }}><Btn onClick={onClose}>Done</Btn></div>
          </>
        ) : (
          <>
            <p style={{ fontSize: 13, color: T.charcoal, marginBottom: 14 }}>Reset password for <strong>{userLabel}</strong>.</p>
            {error && <div style={{ background: T.redBg, color: T.red, padding: 10, borderRadius: T.radiusSm, fontSize: 12, marginBottom: 12 }}>{error}</div>}
            <label style={{ fontSize: 10.5, fontWeight: 600, color: T.gray400, textTransform: "uppercase", letterSpacing: "0.08em", display: "block", marginBottom: 4 }}>New Password</label>
            <Input value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Strong password" style={{ width: "100%", marginBottom: 10 }} />
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: T.charcoal, cursor: "pointer" }}>
              <input type="checkbox" checked={forceChange} onChange={(e) => setForceChange(e.target.checked)} style={{ accentColor: T.purple }} />
              Force password change at next sign-in
            </label>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 14 }}>
              <Btn variant="secondary" onClick={onClose} disabled={busy}>Cancel</Btn>
              <Btn onClick={submit} disabled={busy || !newPassword}>{busy ? "Resetting…" : "Reset"}</Btn>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

function DeleteUserModal({ open, clientId, user, onClose, onDeleted }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const submit = async () => {
    setBusy(true); setError(null);
    try { await api.deleteClientUser(clientId, user.id); onDeleted(); }
    catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };
  return (
    <Modal open={open} onClose={onClose} title="Delete User" width={460}>
      <div style={{ fontFamily: T.font }}>
        <div style={{ background: T.redBg, color: T.red, padding: 12, borderRadius: T.radiusSm, fontSize: 13, fontWeight: 600, marginBottom: 12 }}>This permanently deletes {user?.displayName || user?.userPrincipalName}.</div>
        <p style={{ fontSize: 12.5, color: T.charcoal, lineHeight: 1.6, marginBottom: 12 }}>The user goes to the deleted-users pool for 30 days and can be restored from there. Their license assignments and group memberships are removed.</p>
        {error && <div style={{ background: T.redBg, color: T.red, padding: 10, borderRadius: T.radiusSm, fontSize: 12, marginBottom: 12 }}>{error}</div>}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <Btn variant="secondary" onClick={onClose} disabled={busy}>Cancel</Btn>
          <Btn variant="danger" onClick={submit} disabled={busy}>{busy ? "Deleting…" : "Confirm Delete"}</Btn>
        </div>
      </div>
    </Modal>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  Groups
// ═══════════════════════════════════════════════════════════════════════════
function GroupsTab({ clientId }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorHint, setErrorHint] = useState(null);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [addUserInput, setAddUserInput] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setErrorHint(null);
    try {
      const d = await api.listClientGroups(clientId);
      setGroups(d.groups || []);
    } catch (err) {
      setError(err.message);
      setErrorHint(err.hint || null);
    } finally {
      setLoading(false);
    }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const selected = useMemo(() => groups.find((g) => g.id === selectedId) || null, [groups, selectedId]);
  const filtered = useMemo(() => {
    if (!search) return groups;
    const s = search.toLowerCase();
    return groups.filter((g) =>
      (g.displayName || "").toLowerCase().includes(s) ||
      (g.description || "").toLowerCase().includes(s)
    );
  }, [groups, search]);

  const loadMembers = useCallback(async () => {
    if (!selectedId) return;
    setMembersLoading(true);
    try {
      const d = await api.listClientGroupMembers(clientId, selectedId);
      setMembers(d.members || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setMembersLoading(false);
    }
  }, [clientId, selectedId]);
  useEffect(() => { if (selectedId) loadMembers(); else setMembers([]); }, [selectedId, loadMembers]);

  const addMember = async () => {
    if (!addUserInput.trim()) return;
    setBusy(true); setError(null);
    try {
      await api.addClientGroupMember(clientId, selectedId, addUserInput.trim());
      setAddUserInput("");
      await loadMembers();
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  const removeMember = async (userId) => {
    if (!window.confirm("Remove this member from the group?")) return;
    setBusy(true); setError(null);
    try {
      await api.removeClientGroupMember(clientId, selectedId, userId);
      await loadMembers();
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  const groupType = (g) => {
    if (g.groupTypes && g.groupTypes.includes("Unified")) return "Microsoft 365";
    if (g.securityEnabled && !g.mailEnabled) return "Security";
    if (g.mailEnabled && !g.securityEnabled) return "Distribution";
    return "Other";
  };

  if (loading) return <div style={{ display: "flex", justifyContent: "center", padding: 40 }}><Spinner /></div>;
  if (error && groups.length === 0) return <ErrorBanner error={error} hint={errorHint} />;

  return (
    <div style={{ display: "grid", gap: 16 }}>
      {error && <ErrorBanner error={error} hint={errorHint} />}
      <div className="pf-stack-mobile" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <SectionCard
          title="Groups"
          badge={<span style={{ fontSize: 11, color: T.gray400 }}>{filtered.length} / {groups.length}</span>}
          action={<Btn size="sm" onClick={() => setCreateOpen(true)}>+ New Group</Btn>}
        >
          <Input placeholder="Search groups…" value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: "100%", marginBottom: 10 }} />
          <div style={{ maxHeight: 480, overflowY: "auto" }}>
            {filtered.length === 0 ? <EmptyHint note="No groups match." /> : filtered.map((g) => (
              <button key={g.id} onClick={() => setSelectedId(g.id)} style={{
                width: "100%", textAlign: "left", padding: "10px 12px", border: "none",
                background: selectedId === g.id ? "#F8F5FF" : "transparent",
                borderBottom: `1px solid ${T.gray100}`, cursor: "pointer", fontFamily: T.font,
              }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: T.indigo }}>{g.displayName}</div>
                  <Badge status="info" label={groupType(g)} />
                </div>
                {g.description && <div style={{ fontSize: 11, color: T.gray400, marginTop: 2 }}>{g.description}</div>}
              </button>
            ))}
          </div>
        </SectionCard>

        <SectionCard
          title={selected ? selected.displayName : "Select a group"}
          badge={selected && <Badge status="info" label={groupType(selected)} />}
          action={selected && <Btn size="sm" variant="danger" onClick={() => setDeleteOpen(true)}>Delete</Btn>}
        >
          {!selected ? (
            <EmptyHint note="Click a group to view members." />
          ) : (
            <>
              {selected.description && <div style={{ fontSize: 12.5, color: T.charcoal, fontFamily: T.font, marginBottom: 14, lineHeight: 1.5 }}>{selected.description}</div>}
              <div style={{ fontSize: 10.5, fontWeight: 600, color: T.gray400, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>Members ({members.length})</div>
              {membersLoading ? (
                <div style={{ display: "flex", justifyContent: "center", padding: 16 }}><Spinner size={18} /></div>
              ) : members.length === 0 ? (
                <EmptyHint note="No members in this group." />
              ) : (
                <div style={{ maxHeight: 280, overflowY: "auto", border: `1px solid ${T.gray200}`, borderRadius: T.radiusSm, marginBottom: 12 }}>
                  {members.map((m) => (
                    <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", borderBottom: `1px solid ${T.gray100}` }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12.5, fontWeight: 600, color: T.indigo, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.displayName || m.userPrincipalName}</div>
                        <div style={{ fontSize: 11, color: T.gray400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.userPrincipalName || m.mail || m.id}</div>
                      </div>
                      <Btn size="sm" variant="danger" onClick={() => removeMember(m.id)} disabled={busy}>Remove</Btn>
                    </div>
                  ))}
                </div>
              )}
              <div style={{ display: "flex", gap: 8 }}>
                <Input placeholder="Paste user object ID…" value={addUserInput} onChange={(e) => setAddUserInput(e.target.value)} style={{ flex: 1 }} />
                <Btn size="sm" onClick={addMember} disabled={busy || !addUserInput.trim()}>Add Member</Btn>
              </div>
              <div style={{ fontSize: 10.5, color: T.gray400, marginTop: 6 }}>Object IDs can be copied from the Users tab.</div>
            </>
          )}
        </SectionCard>
      </div>

      <CreateGroupModal open={createOpen} clientId={clientId} onClose={() => setCreateOpen(false)} onCreated={(g) => { setCreateOpen(false); load(); setSelectedId(g.id); }} />
      <DeleteGroupModal open={deleteOpen} clientId={clientId} group={selected} onClose={() => setDeleteOpen(false)} onDeleted={() => { setDeleteOpen(false); setSelectedId(null); load(); }} />
    </div>
  );
}

function CreateGroupModal({ open, clientId, onClose, onCreated }) {
  const [form, setForm] = useState({ displayName: "", mailNickname: "", description: "", groupType: "security" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => { if (open) { setForm({ displayName: "", mailNickname: "", description: "", groupType: "security" }); setError(null); } }, [open]);
  const submit = async () => {
    setBusy(true); setError(null);
    try {
      const { group } = await api.createClientGroup(clientId, form);
      onCreated(group);
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };
  return (
    <Modal open={open} onClose={onClose} title="Create Group" width={520}>
      <div style={{ fontFamily: T.font, display: "grid", gap: 10 }}>
        {error && <div style={{ background: T.redBg, color: T.red, padding: 10, borderRadius: T.radiusSm, fontSize: 12 }}>{error}</div>}
        <div>
          <label style={{ fontSize: 10.5, fontWeight: 600, color: T.gray400, textTransform: "uppercase", letterSpacing: "0.08em", display: "block", marginBottom: 4 }}>Display Name *</label>
          <Input value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })} style={{ width: "100%" }} />
        </div>
        <div>
          <label style={{ fontSize: 10.5, fontWeight: 600, color: T.gray400, textTransform: "uppercase", letterSpacing: "0.08em", display: "block", marginBottom: 4 }}>Mail Nickname *</label>
          <Input value={form.mailNickname} onChange={(e) => setForm({ ...form, mailNickname: e.target.value })} style={{ width: "100%" }} placeholder="alphanumeric, no spaces" />
        </div>
        <div>
          <label style={{ fontSize: 10.5, fontWeight: 600, color: T.gray400, textTransform: "uppercase", letterSpacing: "0.08em", display: "block", marginBottom: 4 }}>Description</label>
          <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} style={{ width: "100%" }} />
        </div>
        <div>
          <label style={{ fontSize: 10.5, fontWeight: 600, color: T.gray400, textTransform: "uppercase", letterSpacing: "0.08em", display: "block", marginBottom: 4 }}>Group Type</label>
          <div style={{ display: "flex", gap: 8 }}>
            {[["security", "Security"], ["microsoft365", "Microsoft 365"]].map(([val, label]) => (
              <button key={val} onClick={() => setForm({ ...form, groupType: val })} style={{
                flex: 1, padding: "10px 12px", borderRadius: T.radiusSm,
                border: `2px solid ${form.groupType === val ? T.purple : T.gray200}`,
                background: form.groupType === val ? "#F8F5FF" : T.white,
                cursor: "pointer", fontSize: 12.5, fontWeight: 600, color: T.indigo, fontFamily: T.font,
              }}>{label}</button>
            ))}
          </div>
          <div style={{ fontSize: 11, color: T.gray400, marginTop: 4 }}>{form.groupType === "microsoft365" ? "Mail-enabled, supports Teams + SharePoint + shared mailbox" : "Security-only, for permissions and access control"}</div>
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 10 }}>
          <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
          <Btn onClick={submit} disabled={busy || !form.displayName || !form.mailNickname}>{busy ? "Creating…" : "Create Group"}</Btn>
        </div>
      </div>
    </Modal>
  );
}

function DeleteGroupModal({ open, clientId, group, onClose, onDeleted }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const submit = async () => {
    setBusy(true); setError(null);
    try { await api.deleteClientGroup(clientId, group.id); onDeleted(); }
    catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };
  return (
    <Modal open={open} onClose={onClose} title="Delete Group" width={460}>
      <div style={{ fontFamily: T.font }}>
        <div style={{ background: T.redBg, color: T.red, padding: 12, borderRadius: T.radiusSm, fontSize: 13, fontWeight: 600, marginBottom: 12 }}>This permanently deletes {group?.displayName}.</div>
        <p style={{ fontSize: 12.5, color: T.charcoal, lineHeight: 1.6, marginBottom: 12 }}>Members are not deleted — only the group itself and its membership relationships. M365 groups also delete their associated mailbox, SharePoint site, and Teams (if any) after 30 days.</p>
        {error && <div style={{ background: T.redBg, color: T.red, padding: 10, borderRadius: T.radiusSm, fontSize: 12, marginBottom: 12 }}>{error}</div>}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <Btn variant="secondary" onClick={onClose} disabled={busy}>Cancel</Btn>
          <Btn variant="danger" onClick={submit} disabled={busy}>{busy ? "Deleting…" : "Confirm Delete"}</Btn>
        </div>
      </div>
    </Modal>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  Page
// ═══════════════════════════════════════════════════════════════════════════
const TABS = [
  { id: "users", label: "Users" },
  { id: "groups", label: "Groups" },
  { id: "licenses", label: "Licenses" },
  { id: "roles", label: "Roles" },
  { id: "signin", label: "Sign-in Logs" },
  { id: "audit", label: "Audit Logs" },
  { id: "mail", label: "Message Trace" },
];

export default function ClientManagement() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState("users");
  const [client, setClient] = useState(null);

  useEffect(() => {
    api.getClient(id).then(setClient).catch(() => {});
  }, [id]);

  return (
    <div style={{ animation: "pfFadeIn 0.3s ease-out" }}>
      <button onClick={() => navigate(`/dashboard/clients/${id}`)} style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", color: T.purple, fontSize: 13, fontWeight: 600, fontFamily: T.font, marginBottom: 16 }}>← Back to Client</button>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: T.indigo, fontFamily: T.font, margin: 0 }}>{client?.name || "Loading…"}</h1>
          <p style={{ fontSize: 13, color: T.gray400, fontFamily: T.font, marginTop: 4 }}>Tenant Administration · {client?.tenantDisplayName || client?.domain || "—"}</p>
        </div>
      </div>

      <div style={{ display: "flex", gap: 4, marginBottom: 20, borderBottom: `1px solid ${T.gray200}`, paddingBottom: 8 }}>
        {TABS.map((t) => <Tab key={t.id} active={tab === t.id} label={t.label} onClick={() => setTab(t.id)} />)}
      </div>

      {tab === "users" && <UsersTab clientId={id} />}
      {tab === "groups" && <GroupsTab clientId={id} />}
      {tab === "licenses" && <LicensesTab clientId={id} />}
      {tab === "roles" && <RolesTab clientId={id} />}
      {tab === "signin" && <LogsTab clientId={id} kind="signin" />}
      {tab === "audit" && <LogsTab clientId={id} kind="audit" />}
      {tab === "mail" && <MailTraceTab clientId={id} />}

      <style>{`@keyframes pfSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
